import {
  download,
  FlexColumn,
  FlexRow,
  getFileNameFromS3Url,
  ImageThumbnail,
  MaterialIcon,
  VideoThumbnail,
} from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { Button } from 'baseui/button';
import { StyledDivider } from 'baseui/divider';
import { ChevronLeft, ChevronRight } from 'baseui/icon';
import { Modal, ModalBody } from 'baseui/modal';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FILETYPE_PICTURE, FILETYPE_VIDEO } from '../../../constants/app';
import { useStyles } from '../../../hooks/useStyles';
import { EventEvidenceFile } from '../../../types/gpt';
import { StyledTooltip } from '../../ui/StyledTooltip';

type Props = Readonly<{
  onClose: () => void;
  isOpen: boolean;
  evidenceFiles: EventEvidenceFile[];
  selectedItemIndex: number;
}>;

function GptEventEvicencesPreviewModal({ onClose, isOpen, evidenceFiles, selectedItemIndex }: Props) {
  const { theme, css, concatenateClasses } = useStyles();
  const [current, setCurrent] = useState(selectedItemIndex);
  const [canShowVideo, setCanShowVideo] = useState(false);
  const evidence = evidenceFiles[current];
  const hasMultipleEvidences = evidenceFiles.length > 1;

  useEffect(() => {
    if (hasMultipleEvidences) {
      setTimeout(() => {
        const elem = document.getElementById(`evidence-${current}`);
        elem?.scrollIntoView();
      }, 0);
    }
  }, [current]);

  useEffect(() => {
    if (evidence.fileType === FILETYPE_VIDEO) {
      setCanShowVideo(false);
      setTimeout(() => {
        setCanShowVideo(true);
      }, 0);
    }
  }, [evidence]);

  const controlStyles = css({
    position: 'absolute',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    width: '100px',
    justifyContent: 'center',
    cursor: 'pointer',
    background: 'transparent',
    color: theme.colors.contentInversePrimary,
  });

  const moveCurrent = (to: 'forward' | 'backward') => {
    const maxEvidenceIndex = evidenceFiles.length - 1;
    if (to === 'forward') {
      if (current + 1 <= maxEvidenceIndex) {
        setCurrent((prev) => prev + 1);
      } else {
        setCurrent(0);
      }
      return;
    }
    if (current - 1 >= 0) {
      setCurrent((prev) => prev - 1);
    } else {
      setCurrent(maxEvidenceIndex);
    }
  };

  const downloadEvidence = async () => {
    if (!evidence.url) return;
    const fileName = getFileNameFromS3Url(evidence.url);
    if (fileName) download(evidence.url, fileName);
  };

  return createPortal(
    <Modal
      animate
      closeable
      onClose={onClose}
      isOpen={isOpen}
      size="auto"
      overrides={{
        Dialog: {
          style: {
            minHeight: '300px',
            minWidth: '300px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          },
        },
      }}
    >
      <ModalBody
        $style={{
          marginTop: theme.sizing.scale900,
          marginBottom: theme.sizing.scale900,
          maxHeight: '85vh',
          overflow: 'hidden',
          width: '90vw',
        }}
      >
        <FlexColumn classNames={css({ overflow: 'hidden' })}>
          <FlexRow
            centered
            classNames={css({ position: 'relative', background: theme.colors.backgroundInversePrimary })}
          >
            {evidence.fileType === FILETYPE_PICTURE && (
              <img
                src={evidence.url}
                className={css({ maxWidth: '100%', height: '70vh', objectFit: 'contain' })}
                alt=""
              />
            )}
            {evidence.fileType === FILETYPE_VIDEO && (
              <>
                {!canShowVideo && <div className={css({ width: '100%', height: '70vh' })} />}
                {canShowVideo && (
                  <video className={css({ maxWidth: '100%', height: '70vh', objectFit: 'contain' })} controls autoPlay>
                    <source src={evidence.url} type="video/mp4" />
                  </video>
                )}
              </>
            )}
            {hasMultipleEvidences && (
              <>
                <Block
                  className={concatenateClasses(
                    controlStyles,
                    css({
                      right: '0',
                      ':hover': {
                        background: 'linear-gradient(270deg, #292929, transparent)',
                      },
                    }),
                  )}
                  onClick={() => moveCurrent('forward')}
                >
                  <ChevronRight size={70} style={{ paddingLeft: theme.sizing.scale600 }} />
                </Block>
                <Block
                  className={concatenateClasses(
                    controlStyles,
                    css({
                      left: '0',
                      ':hover': {
                        background: 'linear-gradient(90deg, #292929, transparent)',
                      },
                    }),
                  )}
                  onClick={() => moveCurrent('backward')}
                >
                  <ChevronLeft size={70} style={{ paddingRight: theme.sizing.scale600 }} />
                </Block>
              </>
            )}
          </FlexRow>
          {hasMultipleEvidences && (
            <FlexRow classNames={css({ overflowX: 'auto' })}>
              {evidenceFiles.map((evidence, index) => {
                if (!evidence.url) return null;

                return (
                  <div
                    id={`evidence-${index}`}
                    key={index}
                    className={css({
                      cursor: 'pointer',
                      opacity: index === current ? 1 : 0.7,
                      transition: 'opacity 0.3s ease',
                      minWidth: '200px',
                    })}
                    onClick={() => setCurrent(index)}
                  >
                    {evidence.fileType === FILETYPE_PICTURE && (
                      <ImageThumbnail
                        url={evidence.url}
                        imageStyle={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'contain',
                        }}
                      />
                    )}
                    {evidence.fileType === FILETYPE_VIDEO && (
                      <VideoThumbnail
                        url={evidence.url}
                        videoStyle={{ width: '100%', height: '100px', objectFit: 'contain' }}
                      />
                    )}
                  </div>
                );
              })}
            </FlexRow>
          )}
        </FlexColumn>
        <StyledDivider $size="cell" style={{ marginBottom: 0, width: '100%' }} />
        <FlexRow>
          <StyledTooltip content={'Descargar'} showArrow={false}>
            <Button size="mini" shape="square" onClick={downloadEvidence} kind="tertiary">
              <MaterialIcon name="download" />
            </Button>
          </StyledTooltip>
        </FlexRow>
      </ModalBody>
    </Modal>,
    document.body,
  );
}

export default GptEventEvicencesPreviewModal;
